import type { ParagraphServiceHero as ParagraphServiceHeroData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphServiceHero.module.css";

/**
 * Layout 38 - `PARAGRAPH_SERVICE_HERO_LAYOUT`: hero subpage uslugi.
 *
 * Struktura:
 *   - Grid 2-kol: content po lewej / dark contact card po prawej
 *   - Lewa kolumna:
 *       - Eyebrow (text_1) - uppercase label akcent (np. "E-COMMERCE")
 *       - H2 (name) - tytul uslugi (np. "Sklepy internetowe")
 *       - Lead (text) - opis pod tytulem
 *       - "Zaufali nam" (text_2) - podnaglowek + grid logotypow (jesli sa)
 *   - Prawa kolumna - dark contact card:
 *       - Zdjecie osoby jako tlo (image_url)
 *       - Overlay: title + author (name+role)
 *       - Zielony CTA button (cta_label + cta_url)
 *       - Kontakt: label + telefon (z ikona)
 */
export function ParagraphServiceHero({
  paragraph,
}: {
  paragraph: ParagraphServiceHeroData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: lead, text_2: trustedHeading, trusted_by, card } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasTrustedHeading = trustedHeading && trustedHeading.trim() !== "";
  const hasLogos = Array.isArray(trusted_by) && trusted_by.length > 0;
  const hasCta = card.cta_label && card.cta_label.trim() !== "" && card.cta_url && card.cta_url.trim() !== "";
  const hasContact = card.contact_label || card.contact_phone;

  return (
    <section className={styles.section}>
      <div className={styles.content}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasLead ? (
          <HtmlText html={lead} className={styles.lead} />
        ) : null}

        {(hasTrustedHeading || hasLogos) ? (
          <div className={styles.trustedBy}>
            {hasTrustedHeading ? (
              <p className={styles.trustedHeading}>{trustedHeading}</p>
            ) : null}
            {hasLogos ? (
              <div className={styles.logosRow}>
                {trusted_by.map((logo) => (
                  <span key={logo.id ?? logo.logo_url} className={styles.logoTile}>
                    <img
                      src={logo.logo_url}
                      alt={logo.name}
                      className={styles.logoImg}
                      loading="lazy"
                    />
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <aside className={styles.card}>
        <div className={styles.cardPhoto}>
          {card.image_url ? (
            <img src={card.image_url} alt="" className={styles.cardBg} loading="lazy" />
          ) : null}
          <div className={styles.cardPhotoOverlay} aria-hidden="true" />
          {card.title ? <p className={styles.cardTitle}>{card.title}</p> : null}
        </div>
        <div className={styles.cardInner}>
          {(card.author_name || card.author_role) ? (
            <div className={styles.cardAuthor}>
              {card.author_name ? <span className={styles.cardAuthorName}>{card.author_name}</span> : null}
              {card.author_role ? <span className={styles.cardAuthorRole}>{card.author_role}</span> : null}
            </div>
          ) : null}
          {hasCta ? (
            <a href={card.cta_url} className={styles.cardCta}>{card.cta_label}</a>
          ) : null}
          {hasContact ? (
            <div className={styles.cardContact}>
              <i className={`nc-icon-outline ui-3_phone ${styles.cardContactIcon}`} aria-hidden="true" />
              <div className={styles.cardContactText}>
                {card.contact_label ? (
                  <span className={styles.cardContactLabel}>{card.contact_label}</span>
                ) : null}
                {card.contact_phone ? (
                  <a href={`tel:${card.contact_phone.replace(/\s+/g, "")}`} className={styles.cardContactPhone}>
                    {card.contact_phone}
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </aside>
    </section>
  );
}
